import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { denormalisedResponseEntities } from '../util/data';
import { storableError } from '../util/errors';
import { setCurrentUser } from './user.duck';

/**
 * My trade-in — the vehicle a buyer already owns and may put toward their next
 * one.
 *
 * Stored on the user's own profile under `privateData.tradeIn`. Private scope
 * matters here: payout and lender are the buyer's financial position, not
 * marketplace data, and `publicData` would expose them on their public
 * profile. This is deliberately NOT a listing — the vehicle is not for sale on
 * Skip, it is context for a deal.
 *
 * Reads come free: `sdk.currentUser.show()` already returns the full profile
 * including privateData, so the record is on `currentUser` app-wide and needs
 * no fetch of its own. Only the write lives here.
 *
 * This has its own slice rather than reusing ProfileSettingsPage's
 * updateProfile thunk, which would drive that page's in-progress/error state
 * from a topbar interaction.
 */

// Only these keys are persisted. Anything else the form happens to carry
// (validation scratch, UI state) is dropped rather than written to the profile.
const TRADE_IN_FIELDS = [
  'vin',
  'year',
  'make',
  'model',
  'odometer',
  'estimatedValue',
  'payout',
  'lender',
  'loanStanding',
];

const NUMERIC_FIELDS = ['year', 'odometer', 'estimatedValue', 'payout'];

/**
 * Reads the saved trade-in off the current user, or null when there is none.
 * @param {Object} currentUser
 * @returns {Object|null}
 */
export const getTradeIn = currentUser => {
  const tradeIn = currentUser?.attributes?.profile?.privateData?.tradeIn;
  return tradeIn && typeof tradeIn === 'object' ? tradeIn : null;
};

/**
 * A short "2018 Chevrolet Silverado" style label, or null when there is not
 * enough saved to name the vehicle.
 */
export const getTradeInLabel = tradeIn => {
  if (!tradeIn) return null;
  const label = [tradeIn.year, tradeIn.make, tradeIn.model].filter(Boolean).join(' ');
  return label || null;
};

/**
 * Negative equity on the saved trade-in: what is still owed beyond what the
 * vehicle is worth. Returns null when either figure is missing rather than
 * treating an unknown as zero, which would understate the buyer's position.
 */
export const getTradeInNegativeEquity = tradeIn => {
  const value = Number(tradeIn?.estimatedValue);
  const payout = Number(tradeIn?.payout);
  if (!Number.isFinite(value) || !Number.isFinite(payout)) return null;
  return Math.max(0, payout - value);
};

export const normalizeTradeIn = values => {
  const record = {};
  TRADE_IN_FIELDS.forEach(key => {
    const raw = values?.[key];
    if (raw === undefined || raw === null || String(raw).trim() === '') return;
    if (NUMERIC_FIELDS.includes(key)) {
      const n = typeof raw === 'string' ? Number(raw.replace(/[^0-9.-]/g, '')) : Number(raw);
      if (Number.isFinite(n)) record[key] = n;
    } else {
      record[key] = String(raw).trim();
    }
  });
  record.updatedAt = new Date().toISOString();
  return record;
};

export const saveTradeInPayloadCreator = async (values, thunkAPI) => {
  const { extra: sdk, dispatch, rejectWithValue } = thunkAPI;

  try {
    const response = await sdk.currentUser.updateProfile(
      { privateData: { tradeIn: normalizeTradeIn(values) } },
      { expand: true }
    );
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
    }
    // Keeps currentUser (and therefore every reader of the trade-in) in sync
    // without a refetch.
    dispatch(setCurrentUser(entities[0]));
    return true;
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const saveTradeInThunk = createAsyncThunk('app/tradeIn/save', saveTradeInPayloadCreator);

export const saveTradeIn = values => dispatch => dispatch(saveTradeInThunk(values));

const initialState = {
  saveInProgress: false,
  saveError: null,
  saved: false,
};

const tradeInSlice = createSlice({
  name: 'app/tradeIn',
  initialState,
  reducers: {
    resetTradeInSaveState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(saveTradeInThunk.pending, state => {
        state.saveInProgress = true;
        state.saveError = null;
        state.saved = false;
      })
      .addCase(saveTradeInThunk.fulfilled, state => {
        state.saveInProgress = false;
        state.saved = true;
      })
      .addCase(saveTradeInThunk.rejected, (state, action) => {
        state.saveInProgress = false;
        state.saveError = action.payload;
        state.saved = false;
      });
  },
});

export const { resetTradeInSaveState } = tradeInSlice.actions;
export default tradeInSlice.reducer;
